import React, { useState } from "react";
import styled from "styled-components/macro";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import {
  PatienReleaseIndicator,
  ReportStatusPrimary,
  ReportStatusSecondary,
  ReportStatusStack,
} from "./Grid.styles";
import { getIcon } from "./ReportStatusIcon";
import { CellRendererProps } from "./types";
import { StatusIconSlot } from "../shared/StatusIconSlot.styles";
import { AddOnSelection, AddOnSelectionOption } from "./AddOnSelection";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

const CellWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled(StatusIconSlot)`
  margin-right: 8px;
`;

const SmallAddOnButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-left: 15px;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  background: #ceeae3;
  cursor: pointer;
  &:hover {
    background: #bbd8d1;
  }
  svg {
    color: #017e72;
    height: 12px;
    width: 12px;
  }
`;

const RequestDataCell: React.FC<CellRendererProps> = ({
  value = "",
  data,
  colDef,
  node,
  api,
  tabType,
  deviceType,
  setAddOnModal,
}) => {
  const { auth } = useAuthenticationHook();
  const { enabled: smallAddOnButton } = useBooleanFeatureFlag(
    FeatureFlags.SmallAddOnButton,
  );
  const { enabled: fetalAntigenLateAdd } = useBooleanFeatureFlag(
    FeatureFlags.FetalAntigenLateAdd,
  );
  const { enabled: fetalAntigenExpansion } = useBooleanFeatureFlag(
    FeatureFlags.FetalAntigenExpansion,
  );
  const [selectionAnchorEl, setSelectionAnchorEl] =
    useState<HTMLElement | null>(null);
  const isFetalSex = colDef?.field === "fetalSex";
  const isRhd = colDef?.field === "rhd";
  const rhdProcessing = isRhd && data.rhdReleaseStatus === "Processing";
  const faProcessing = isRhd && data.faReleaseStatus === "Processing";
  const hasCompletedRhdResult = ["Final", "Corrected"].includes(
    data.rhdReleaseStatus,
  );

  const valueText = typeof value === "string" ? value : "";
  let statusText = valueText;
  if (isRhd) {
    statusText = data.RhD ?? "";
  }
  // If there's a complete RhD result, fa processing shows as subtext
  if (rhdProcessing || (faProcessing && !hasCompletedRhdResult)) {
    statusText = "Processing";
  }
  const { icon, styles } = getIcon(statusText);

  const isFetalSexEligible =
    isFetalSex && data.fetalReleaseStatus === "Request";
  const isRhdEligible = isRhd && data.rhdReleaseStatus === "Request";
  const isFetalAntigenEligible =
    isRhd &&
    fetalAntigenLateAdd &&
    fetalAntigenExpansion &&
    data.faReleaseStatus === "Request";

  const showButton =
    colDef &&
    node &&
    setAddOnModal &&
    auth?.user_type !== "sales_rep" &&
    tabType !== "ARCHIVED" &&
    (isFetalSexEligible || isRhdEligible || isFetalAntigenEligible);

  const selectionOptions: AddOnSelectionOption[] = [];
  if (isRhdEligible) {
    selectionOptions.push({ mode: "fetal_rhd", label: "Add Fetal RhD" });
  }
  if (isFetalAntigenEligible) {
    selectionOptions.push({
      mode: "fetal_antigen",
      label: "Add Fetal Antigen",
    });
  }

  let processingSubtext = "";
  if (isRhd) {
    if (rhdProcessing) {
      processingSubtext = faProcessing
        ? "Fetal RhD & Fetal Antigens"
        : "Fetal RhD";
    } else if (faProcessing) {
      processingSubtext = hasCompletedRhdResult
        ? "Processing Fetal Antigens"
        : "Fetal Antigens";
    }
  }

  return (
    <CellWrapper
      onClick={() => {
        if (deviceType === "desktop") {
          const currentPage = api?.paginationGetCurrentPage();
          localStorage?.setItem(
            `btoProviderCurrentPage${tabType}`,
            JSON.stringify(currentPage),
          );
        }
      }}
    >
      {icon && <IconWrapper>{icon}</IconWrapper>}
      {processingSubtext ? (
        <ReportStatusStack>
          <ReportStatusPrimary style={styles}>{statusText}</ReportStatusPrimary>
          <ReportStatusSecondary>{processingSubtext}</ReportStatusSecondary>
        </ReportStatusStack>
      ) : (
        <span style={styles}>{statusText}</span>
      )}
      {showButton &&
        (smallAddOnButton ? (
          <Tooltip title="Request" arrow placement="top">
            <SmallAddOnButton
              onClick={(e) => {
                e.stopPropagation();
                if (isFetalSex) {
                  setAddOnModal!({ row: data, node, type: "fetal_sex" });
                } else {
                  setSelectionAnchorEl(e.currentTarget);
                }
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </SmallAddOnButton>
          </Tooltip>
        ) : (
          <PatienReleaseIndicator
            onClick={(e) => {
              e.stopPropagation();
              if (isFetalSex) {
                setAddOnModal!({ row: data, node, type: "fetal_sex" });
              } else {
                setSelectionAnchorEl(e.currentTarget);
              }
            }}
            button
          >
            Request
          </PatienReleaseIndicator>
        ))}
      {showButton && !isFetalSex && (
        <AddOnSelection
          anchorEl={selectionAnchorEl}
          options={selectionOptions}
          onClose={() => setSelectionAnchorEl(null)}
          onSelect={(mode) => {
            setSelectionAnchorEl(null);
            setAddOnModal!({ row: data, node, type: mode });
          }}
        />
      )}
    </CellWrapper>
  );
};

export default RequestDataCell;
